import {Controller, useForm} from 'react-hook-form';
import React from 'react';
import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from '@material-ui/core';
import {v4 as uuid} from 'uuid';
import {GOOGLE_API_KEY} from 'constants/index';
import GooglePlacesLocationSearch from 'components/GooglePlacesLocationSearch/GooglePlacesLocationSearch';
import {IRegion} from 'constants/interfaces';

export type CreateFarmerFormSubmission = {
  name: string;
  surname: string;
  email: string;
  phone: string;
  username: string;
  password: string;
  address: string;
  latlng: string;
  province: string;
  country: string;
  farmName: string;
};

export function CreateFarmerForm(props: {
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (submission: CreateFarmerFormSubmission) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
}) {
  type Submission = CreateFarmerFormSubmission & {
    confirmNewPassword: string;
  };

  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control, setValue} = useForm<Submission>();

  const handleRegionChange = (region?: IRegion) => {
    setValue('address', region?.address!);
    setValue('latlng', region?.latlong!);
    setValue('province', region?.province!);
    setValue('country', region?.country!);
  };

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      name: submission.name,
      surname: submission.surname,
      phone: submission.phone,
      email: submission.email,
      username: submission.email,
      password: uuid(),
      address: submission.address,
      latlng: submission.latlng,
      province: submission.province,
      country: submission.country,
      farmName: submission.farmName,
    });
  };

  return (
    <div className="generic-form-container">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="First Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                  />
                )}
                rules={{
                  required: 'First name required',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="surname"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Last Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                  />
                )}
                rules={{
                  required: 'Last name required',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="email"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Email"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                  />
                )}
                rules={{
                  required: 'Email required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Valid email address required',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="phone"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Phone"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                  />
                )}
                rules={{
                  required: 'Phone required',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="address"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <FormControl error={!!error} fullWidth>
                    <GooglePlacesLocationSearch
                      placeholder="Address"
                      queryCountries={['za']}
                      stylesInput={{height: 45, marginLeft: 0}}
                      googleApiKey={GOOGLE_API_KEY}
                      value={value}
                      onSelect={(region: IRegion) => {
                        onChange(region.address);
                        handleRegionChange(region);
                      }}
                      onCleared={() => {
                        onChange(undefined);
                        handleRegionChange(undefined);
                      }}
                    />
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
                rules={{
                  required: 'Address required',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="farmName"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Company Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                  />
                )}
                rules={{
                  required: 'Company name required',
                }}
              />
            </Grid>
            <Grid
              container
              spacing={2}
              style={{paddingTop: '1rem', paddingBottom: '1rem'}}
            >
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className={'secondary-btn'}
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className={'primary-btn'}
                  type="submit"
                  variant="contained"
                  disableElevation
                  fullWidth
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress
                      color={'primary'}
                      size={24}
                      thickness={6}
                    />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
